import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchLayoutV2,
  saveLayoutV2,
  addWidgetV2,
  updateWidgetPositionsV2,
  updateWidgetConfigV2,
  deleteWidgetV2,
  fetchWidgetDataV2,
  fetchBatchWidgetDataV2,
  setDashboardV2Error
} from '../actions/dashboardV2';

export const useDashboardV2 = () => {
  const dispatch = useDispatch();
  const [refreshing, setRefreshing] = useState(false);

  const {
    loading,
    error,
    layout,
    widgetData,
    isLoading
  } = useSelector(state => state.dashboardV2Reducer);

  // Cargar layout inicial
  useEffect(() => {
    loadLayout();
  }, []);

  // Cargar layout
  const loadLayout = useCallback(async () => {
    try {
      await dispatch(fetchLayoutV2());
    } catch (err) {
      console.error('Error loading layout:', err);
    }
  }, [dispatch]);

  // Recargar todo
  const reload = useCallback(async () => {
    setRefreshing(true);
    try {
      await loadLayout();
    } finally {
      setRefreshing(false);
    }
  }, [loadLayout]);

  // Agregar widget
  const addWidget = useCallback(async (widgetData) => {
    try {
      const result = await dispatch(addWidgetV2(widgetData));
      // No recargar, el reducer ya actualiza el estado
      return result.value?.data?.widget?.widgetId || result.value?.data?.widgetId;
    } catch (err) {
      console.error('Error adding widget:', err);
      throw new Error(err.response?.data?.error || 'Error al agregar widget');
    }
  }, [dispatch]);

  // Actualizar posiciones (debounced en el componente)
  const updatePositions = useCallback(async (widgets) => {
    try {
      await dispatch(updateWidgetPositionsV2(widgets));
    } catch (err) {
      console.error('Error updating positions:', err);
      throw new Error(err.response?.data?.error || 'Error al actualizar posiciones');
    }
  }, [dispatch]);

  // Actualizar configuración de widget
  const updateWidget = useCallback(async (widgetId, config) => {
    try {
      await dispatch(updateWidgetConfigV2(widgetId, config));
      await loadLayout(); // Recargar layout después de actualizar
    } catch (err) {
      console.error('Error updating widget:', err);
      throw new Error(err.response?.data?.error || 'Error al actualizar widget');
    }
  }, [dispatch, loadLayout]);

  // Eliminar widget
  const deleteWidget = useCallback(async (widgetId) => {
    try {
      await dispatch(deleteWidgetV2(widgetId));
      await loadLayout(); // Recargar layout después de eliminar
    } catch (err) {
      console.error('Error deleting widget:', err);
      throw new Error(err.response?.data?.error || 'Error al eliminar widget');
    }
  }, [dispatch, loadLayout]);

  // Obtener datos de un widget específico
  const fetchWidgetData = useCallback(async (widgetId, tipo, params) => {
    try {
      await dispatch(fetchWidgetDataV2(widgetId, tipo, params));
    } catch (err) {
      console.error('Error fetching widget data:', err);
    }
  }, [dispatch]);

  // Obtener datos de múltiples widgets
  const fetchBatchData = useCallback(async (widgetIds) => {
    try {
      await dispatch(fetchBatchWidgetDataV2(widgetIds));
    } catch (err) {
      console.error('Error fetching batch data:', err);
    }
  }, [dispatch]);

  // Limpiar error
  const clearError = useCallback(() => {
    dispatch(setDashboardV2Error(null));
  }, [dispatch]);

  // Guardar layout completo
  const saveLayout = useCallback(async (layoutData) => {
    try {
      await dispatch(saveLayoutV2(layoutData));
      await loadLayout();
    } catch (err) {
      console.error('Error saving layout:', err);
      throw new Error(err.response?.data?.error || 'Error al guardar layout');
    }
  }, [dispatch, loadLayout]);

  return {
    // Estado
    layout,
    loading: loading || isLoading,
    refreshing,
    error,
    widgetData,

    // Métodos
    addWidget,
    updatePositions,
    updateWidget,
    deleteWidget,
    fetchWidgetData,
    fetchBatchData,
    reload,
    clearError,
    saveLayout
  };
};
